import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { TASK_TYPE_MAPPING } from './renewTaskTypes'

/**
 * Hook personnalisé pour filtrer et trier les tâches de renouvellement
 *
 * Logique de filtrage :
 * - Filtre par type de tâche (assurance, diagnostic, entretien, facturation)
 * - Inclut les tâches actives (non completed, non cancelled)
 * - Inclut les tâches en retard + prochains N jours + sans date
 *
 * Logique de tri :
 * - Tâches en retard en premier (jours négatifs)
 * - Puis par jours jusqu'à l'échéance (ascendant)
 * - Tâches sans date à la fin
 *
 * @param {Object} options - Options de filtrage
 * @param {string} options.taskType - Type de tâche ('assurance', 'diagnostic', etc.)
 * @param {number} options.daysWindow - Fenêtre de jours (défaut : 30)
 * @param {number} options.maxTasks - Maximum de tâches à retourner (défaut : 10)
 * @param {Array<string>} options.statuses - Statuts autorisés (défaut : ['pending', 'completed'])
 * @returns {Object} { tasks, loading, typeConfig }
 */
export const useFilteredRenewTasks = ({
  taskType = 'assurance',
  daysWindow = 30,
  maxTasks = 10,
  statuses = ['pending', 'completed']
} = {}) => {
  const allTasks = useSelector(state => state.renewTasks?.items || [])
  const loading = useSelector(state => state.renewTasks?.loadingStatus?.fetchList || false)

  const typeConfig = TASK_TYPE_MAPPING[taskType] || TASK_TYPE_MAPPING.assurance

  const tasks = useMemo(() => {
    return allTasks
      // 1. Filtrer par type de tâche
      .filter(task => typeConfig.types.includes(task.task_type))
      // 2. Filtrer par statut
      .filter(task => statuses.includes(task.status))
      // 3. Filtrer par fenêtre de jours
      // Inclut : en retard (< 0), prochaines (0 à daysWindow), sans date (null)
      // IMPORTANT: Ne pas filtrer les tâches complétées par days_until_due
      .filter(task => {
        if (task.status === 'completed') return true
        return task.days_until_due == null || task.days_until_due <= daysWindow
      })
      // 4. Trier
      .sort((a, b) => {
        // Tâches sans date à la fin
        if (a.days_until_due == null && b.days_until_due == null) return 0
        if (a.days_until_due == null) return 1
        if (b.days_until_due == null) return -1

        // Trier par jours jusqu'à l'échéance (en retard en premier)
        return a.days_until_due - b.days_until_due
      })
      // 5. Limiter la quantité
      .slice(0, maxTasks)
  }, [allTasks, typeConfig, daysWindow, maxTasks, statuses])

  return {
    tasks,
    loading,
    typeConfig
  }
}

/**
 * Custom hook pour obtenir des statistiques sur les tâches de renouvellement 
 *
 * @param {string} taskType - Type de tâche
 * @returns {Object}  Statistiques
 */
export const useRenewTaskStats = (taskType) => {
  const allTasks = useSelector(state => state.renewTasks?.items || [])
  const typeConfig = TASK_TYPE_MAPPING[taskType]

  const stats = useMemo(() => {
    const filtered = allTasks.filter(task => typeConfig?.types.includes(task.task_type))

    return {
      total: filtered.length,
      pending: filtered.filter(t => t.status === 'pending').length,
      in_progress: filtered.filter(t => t.status === 'in_progress').length,
      completed: filtered.filter(t => t.status === 'completed').length,
      overdue: filtered.filter(t => t.overdue && t.status !== 'completed').length,
      upcoming: filtered.filter(t =>
        t.days_until_due != null &&
        t.days_until_due > 0 &&
        t.days_until_due <= 30 &&
        t.status !== 'completed'
      ).length
    }
  }, [allTasks, typeConfig])

  return stats
}

export default useFilteredRenewTasks
